import { useState, useEffect } from 'react';
import type { Consultation, TranslationMap } from '../types/consultation';
import { SPACES_URL, API_CONFIG, useMockData } from '../config/constants';

interface UseConsultationResult {
  config: Consultation | null;
  translations: TranslationMap | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Custom hook for fetching consultation configuration and translations from CDN
 * Implements retry logic with exponential backoff
 */
export function useConsultation(
  consultationId: string,
  language: string = 'en'
): UseConsultationResult {
  const [config, setConfig] = useState<Consultation | null>(null);
  const [translations, setTranslations] = useState<TranslationMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryTrigger, setRetryTrigger] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchWithRetry = async (
      url: string,
      retries: number = API_CONFIG.retries
    ): Promise<Response> => {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            API_CONFIG.requestTimeout
          );

          const response = await fetch(url, {
            signal: controller.signal,
            headers: {
              'Accept': 'application/json',
            },
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            return response;
          }

          // Don't retry on 404 - resource doesn't exist
          if (response.status === 404) {
            throw new Error(`Resource not found: ${url}`);
          }

          // Retry on other error status codes
          if (attempt < retries - 1) {
            const delay = API_CONFIG.retryDelays[attempt];
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } catch (err) {
          if (attempt < retries - 1) {
            // Wait before retrying
            const delay = API_CONFIG.retryDelays[attempt];
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          // Last attempt failed
          throw err;
        }
      }

      throw new Error('Max retries exceeded');
    };

    const fetchConsultation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Determine URLs based on environment
        let configUrl: string;
        let translationsUrl: string;

        if (useMockData) {
          // Development: Use consultations directory (served by Vite middleware)
          // Path structure matches production: consultations/{consultationId}/...
          configUrl = `/consultations/${consultationId}/config.json`;
          translationsUrl = `/consultations/${consultationId}/translations/${language}.json`;
        } else {
          // Staging/Production: Use CDN
          configUrl = `${SPACES_URL}/consultations/${consultationId}/config.json`;
          translationsUrl = `${SPACES_URL}/consultations/${consultationId}/translations/${language}.json`;
        }

        // Fetch both config and translations in parallel
        const [configResponse, translationsResponse] = await Promise.all([
          fetchWithRetry(configUrl),
          fetchWithRetry(translationsUrl).catch(async (err) => {
            // Fallback to English if requested language fails
            if (language !== 'en') {
              console.warn(
                `Failed to load ${language} translations, falling back to English:`,
                err
              );
              const enUrl = useMockData
                ? `/consultations/${consultationId}/translations/en.json`
                : `${SPACES_URL}/consultations/${consultationId}/translations/en.json`;
              return fetchWithRetry(enUrl);
            }
            throw err;
          }),
        ]);

        const configData = await configResponse.json();
        const translationsData = await translationsResponse.json();

        if (!isMounted) return;

        setConfig(configData);
        setTranslations(translationsData);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load consultation';

        console.error('Error fetching consultation:', err);
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchConsultation();

    return () => {
      isMounted = false;
    };
  }, [consultationId, language, retryTrigger]);

  const retry = () => {
    setRetryTrigger((prev) => prev + 1);
  };

  return {
    config,
    translations,
    loading,
    error,
    retry,
  };
}
